import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus } from "lucide-react";

import {
  type Client,
  type ClientCreate,
  clientsApi,
} from "@/api/clients";
import { useCanEdit } from "@/hooks/useRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ClientDetail } from "./clients/ClientDetail";

export function ClientsPage() {
  const { t } = useTranslation();
  const canEdit = useCanEdit();
  const qc = useQueryClient();

  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  const list = useQuery({
    queryKey: ["clients", { includeInactive }],
    queryFn: () => clientsApi.list(includeInactive),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-brand-navy-700">{t("clients.title")}</h1>
          <p className="text-sm text-slate-500">{t("clients.subtitle")}</p>
        </div>
        {canEdit && (
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4" /> {t("clients.create_client")}
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>{t("clients.title")}</CardTitle>
              <label className="inline-flex items-center gap-2 text-xs text-slate-600 pt-1">
                <input type="checkbox" checked={includeInactive}
                  onChange={(e) => setIncludeInactive(e.target.checked)} />
                {t("clients.include_inactive")}
              </label>
            </CardHeader>
            <CardContent>
              {list.isLoading ? (
                <div className="flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" /> {t("common.loading")}
                </div>
              ) : list.data && list.data.length > 0 ? (
                <ul className="divide-y divide-slate-100">
                  {list.data.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(c.id)}
                        className={cn(
                          "w-full text-left py-2 px-2 rounded-md transition-colors",
                          selectedId === c.id ? "bg-brand-navy-50" : "hover:bg-slate-50",
                        )}
                      >
                        <div className="flex items-center justify-between">
                          <div className="font-medium text-slate-800">{c.name}</div>
                          {!c.is_active && (
                            <span className="text-xs text-slate-400">{t("common.inactive")}</span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500">
                          {c.code} · {t(`clients.tariff_type_${c.tariff_type}`)}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-slate-500">{t("common.no_data")}</div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selectedId ? (
            <ClientDetail
              clientId={selectedId}
              onDeactivated={() => {
                setSelectedId(null);
                void qc.invalidateQueries({ queryKey: ["clients"] });
              }}
            />
          ) : (
            <Card>
              <CardContent className="py-12 text-center text-slate-500 text-sm">
                {t("common.no_data")}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {showCreate && (
        <CreateClientModal
          onClose={() => setShowCreate(false)}
          onCreated={(c) => {
            setShowCreate(false);
            setSelectedId(c.id);
            void qc.invalidateQueries({ queryKey: ["clients"] });
          }}
        />
      )}
    </div>
  );
}

// ---------- Create modal ----------

interface CreateClientModalProps {
  onClose: () => void;
  onCreated: (c: Client) => void;
}

function CreateClientModal({ onClose, onCreated }: CreateClientModalProps) {
  const { t } = useTranslation();
  const [form, setForm] = useState<ClientCreate>({
    name: "",
    code: "",
    tariff_type: "fixed",
    trader_fee_pct: 0,
    is_active: true,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => clientsApi.create(form),
    onSuccess: (c) => onCreated(c),
    onError: (e: unknown) => {
      const code = (e as { response?: { data?: { error?: { code?: string } } } })?.response?.data?.error?.code;
      setError(
        code === "client_code_conflict"
          ? t("clients.errors.code_conflict")
          : t("clients.errors.unknown"),
      );
    },
  });

  const valid = form.name.trim() && /^[a-z0-9_]+$/.test(form.code);

  return (
    <div
      className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardHeader>
          <CardTitle>{t("clients.create_client")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="space-y-1">
            <Label>{t("clients.name")}</Label>
            <Input value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t("clients.code")}</Label>
            <Input value={form.code} placeholder="ekosfera"
              onChange={(e) => setForm({ ...form, code: e.target.value.toLowerCase() })} />
            <p className="text-xs text-slate-500">a-z, 0-9, _</p>
          </div>
          <div className="space-y-1">
            <Label>{t("clients.tariff_type")}</Label>
            <select className="w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm"
              value={form.tariff_type}
              onChange={(e) => setForm({ ...form, tariff_type: e.target.value as ClientCreate["tariff_type"] })}>
              <option value="fixed">{t("clients.tariff_type_fixed")}</option>
              <option value="rdn_dynamic">{t("clients.tariff_type_rdn_dynamic")}</option>
            </select>
          </div>
          {form.tariff_type === "rdn_dynamic" && (
            <div className="space-y-1">
              <Label>{t("clients.trader_fee_pct")}</Label>
              <Input type="number" step="0.01" min={0} max={100}
                value={form.trader_fee_pct ?? 0}
                onChange={(e) => setForm({ ...form, trader_fee_pct: Number(e.target.value) })} />
            </div>
          )}
          {error && <div className="text-sm text-red-600">{error}</div>}
        </CardContent>
        <div className="flex gap-2 px-6 pb-6">
          <Button onClick={() => create.mutate()} disabled={!valid || create.isPending}>
            {create.isPending ? t("common.saving") : t("common.create")}
          </Button>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
        </div>
      </Card>
    </div>
  );
}
